(function (scope) {
  const familyOrder = [
    'white',
    'black-grey',
    'red-pink',
    'blue',
    'green',
    'yellow-orange',
    'purple',
    'brown-bronze',
  ];

  function normalizeValue(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function classifyColourFamily(value) {
    const name = normalizeValue(value);
    const includesAny = (...terms) => terms.some((term) => name.includes(term));

    if (includesAny('white', 'ivory', 'cream')) return 'white';
    if (includesAny('black', 'grey', 'gray', 'silver', 'graphite', 'carbon', 'charcoal')) return 'black-grey';
    if (includesAny('red', 'pink', 'rose', 'burgundy', 'cherry', 'maroon', 'magenta')) return 'red-pink';
    if (includesAny('blue', 'navy', 'cyan', 'teal', 'aqua')) return 'blue';
    if (includesAny('green', 'emerald', 'olive', 'mint', 'jade')) return 'green';
    if (includesAny('yellow', 'orange', 'gold', 'lemon', 'amber')) return 'yellow-orange';
    if (includesAny('purple', 'violet', 'lavender', 'lilac', 'plum')) return 'purple';
    if (includesAny('brown', 'bronze', 'copper', 'champagne', 'beige', 'coffee')) return 'brown-bronze';
    return 'other';
  }

  function resolveColourFamily(product) {
    const explicitFamily = normalizeValue(product.family);
    if (familyOrder.includes(explicitFamily) || explicitFamily === 'other') return explicitFamily;
    if (explicitFamily) return classifyColourFamily(explicitFamily);
    return classifyColourFamily(product.name);
  }

  function matchesColour(product, queryValue, activeFamily = 'all') {
    const aliases = { grey: 'gray', colour: 'color', mat: 'matte', matt: 'matte' };
    const words = (value) => (normalizeValue(value).match(/[\p{L}\p{N}]+/gu) || [])
      .map((word) => aliases[word] || word);
    const query = words(queryValue);
    // Search the supplied family, not the legacy combined black-grey bucket.
    const searchable = words([product.name, product.code, product.skus, product.family, product.finish].join(' '));
    const codes = [product.code, ...(Array.isArray(product.skus) ? product.skus : String(product.skus || '').split(/[|,\s]+/))]
      .map((code) => normalizeValue(code).replace(/[^a-z0-9]/g, '')).filter(Boolean);
    const queryMatches = query.every((word) => searchable.some((term) => term.startsWith(word))
      || (/\d/.test(word) && codes.some((code) => code.includes(word))));
    const familyMatches = activeFamily === 'all' || resolveColourFamily(product) === activeFamily;
    return queryMatches && familyMatches;
  }

  class ColourLibrary {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-colour-search]');
      this.cards = [...root.querySelectorAll('[data-colour-card]')];
      this.filters = [...root.querySelectorAll('[data-colour-family-filter]')];
      this.status = root.querySelector('[aria-live]');
      this.activeFamily = 'all';

      if (!this.input) return;
      this.prepareFamilies();
      this.input.addEventListener('input', () => this.filter());
      this.filters.forEach((button) => {
        button.addEventListener('click', () => {
          this.activeFamily = button.dataset.colourFamilyFilter || 'all';
          this.filters.forEach((candidate) => {
            candidate.setAttribute('aria-pressed', String(candidate === button));
          });
          this.filter();
        });
      });
    }

    prepareFamilies() {
      const counts = new Map();
      this.cards.forEach((card) => {
        const family = resolveColourFamily({
          name: card.dataset.colourName,
          family: card.dataset.colourFamily,
        });
        card.dataset.colourFamilyResolved = family;
        counts.set(family, (counts.get(family) || 0) + 1);
      });

      this.filters.forEach((button) => {
        const family = button.dataset.colourFamilyFilter;
        if (family !== 'all') button.hidden = !counts.get(family);
      });
    }

    filter() {
      let visible = 0;

      this.cards.forEach((card) => {
        const matches = matchesColour({
          name: card.dataset.colourName,
          code: card.dataset.colourCode,
          family: card.dataset.colourFamilyResolved,
        }, this.input.value, this.activeFamily);
        card.closest('li').hidden = !matches;
        if (matches) visible += 1;
      });

      this.status.hidden = false;
      this.status.textContent = visible === 0
        ? 'No colours on this page match. Press Enter to search the complete catalogue.'
        : `${visible} ${visible === 1 ? 'colour' : 'colours'} shown on this page.`;
    }
  }

  const browseStates = new WeakMap();

  class CatalogueSearch {
    constructor(root) {
      this.root = root;
      this.host = root.closest('[data-catalogue-host]') || root;
      this.input = root.querySelector('[data-colour-search]');
      this.form = root.querySelector('[data-catalogue-form]');
      this.status = root.querySelector('[data-catalogue-status]');
      this.results = root.querySelector('[data-catalogue-results]');
      this.grid = root.querySelector('[data-catalogue-grid]');
      this.clearButton = root.querySelector('[data-catalogue-clear]');
      this.moreButton = root.querySelector('[data-catalogue-more]');
      this.generation = 0;
      if (!this.input || !this.form || !this.status || !this.results || !this.grid) return;
      // Facets can replace a search root while leaving its host/navigation alive.
      this.setActive(false);
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.timer);
        this.search();
      });
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.generation += 1;
        if (!normalizeValue(this.input.value)) { this.clear(); return; }
        this.setActive(true);
        this.resetResults();
        this.results.hidden = false;
        this.status.hidden = false;
        this.status.textContent = 'Searching the complete catalogue…';
        this.root.setAttribute('aria-busy', 'true');
        this.timer = setTimeout(() => this.search(), 250);
      });
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.clear();
        this.input.focus();
      });
      this.moreButton?.addEventListener('click', () => this.showMore());
      this.resetResults();
      const initialQuery = this.input.value || root.dataset.initialQuery || new URL(scope.location.href).searchParams.get('q');
      if (normalizeValue(initialQuery)) {
        this.input.value = initialQuery;
        this.search();
      }
    }

    setActive(active) {
      this.host.classList.toggle('pw-catalogue-search-active', active);
      if (active) {
        const saved = browseStates.get(this.host) || new Map();
        this.host.querySelectorAll('[data-catalogue-browse], .pw-colour-family-nav, .facets-vertical-sort, #main-collection-filters').forEach((element) => {
          if (!saved.has(element)) saved.set(element, element.hidden);
          element.hidden = true;
        });
        browseStates.set(this.host, saved);
      } else {
        browseStates.get(this.host)?.forEach((hidden, element) => { element.hidden = hidden; });
        browseStates.delete(this.host);
      }
      if (this.clearButton) this.clearButton.hidden = !active;
    }

    clear() {
      this.generation += 1;
      clearTimeout(this.timer);
      this.setActive(false);
      this.resetResults();
      this.results.hidden = true;
      this.status.hidden = true;
      this.status.textContent = '';
      this.root.removeAttribute('aria-busy');
    }

    resetResults() {
      this.matches = [];
      this.rendered = 0;
      this.grid.replaceChildren();
      if (this.moreButton) this.moreButton.hidden = true;
    }

    showMore() {
      const batch = this.matches.slice(this.rendered, this.rendered + 24);
      batch.forEach((product) => this.grid.append(product.template.content.cloneNode(true)));
      this.rendered += batch.length;
      if (this.moreButton) this.moreButton.hidden = this.rendered >= this.matches.length;
      this.status.textContent = `${this.matches.length} ${this.matches.length === 1 ? 'result' : 'results'} in the complete catalogue. Showing ${this.rendered}.`;
    }

    catalogueURL() {
      const current = new URL(scope.location.href);
      const url = new URL(this.root.dataset.catalogueUrl, current);
      if (url.origin !== current.origin || !/\/collections\/[^/]+\/?$/.test(url.pathname)) {
        throw new Error('The catalogue address is invalid.');
      }
      url.search = '';
      url.searchParams.set('view', 'catalogue-data');
      for (const key of ['preview_theme_id', 'preview_key', '_fd', '_ab', 'pb', 'sort_by']) {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      }
      return url;
    }

    pageURL(value, current, base) {
      const url = new URL(value, current);
      if (url.origin !== base.origin || url.pathname !== base.pathname || url.username || url.password) {
        throw new Error('The catalogue returned an unexpected page address.');
      }
      const page = url.searchParams.get('page');
      if (page !== null && !/^[1-9]\d*$/.test(page)) throw new Error('The catalogue page number is invalid.');
      url.search = base.search;
      if (page !== null) url.searchParams.set('page', page);
      url.hash = '';
      return url;
    }

    async fetchPage(url, base) {
      const controller = new scope.AbortController();
      let timer;
      const deadline = new Promise((resolve, reject) => {
        timer = scope.setTimeout(() => {
          controller.abort();
          reject(new Error('The catalogue request timed out.'));
        }, 15000);
      });
      try {
        return await Promise.race([deadline, (async () => {
          const response = await scope.fetch(url.href, {
            credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          });
          if (!response.ok) throw new Error(`The catalogue could not be loaded (HTTP ${response.status}).`);
          if (response.url) this.pageURL(response.url, url, base);
          return response.text();
        })()]);
      } finally {
        scope.clearTimeout(timer);
      }
    }

    async loadCatalogue() {
      const base = this.catalogueURL();
      let url = base;
      const records = new Map();
      const visited = new Set();
      let total;
      do {
        if (!this.root.isConnected) throw new Error('The search section changed.');
        if (visited.has(url.href) || visited.size >= 100) throw new Error('The catalogue pagination could not be verified.');
        visited.add(url.href);
        const html = await this.fetchPage(url, base);
        if (!this.root.isConnected) throw new Error('The search section changed.');
        const document = new scope.DOMParser().parseFromString(html, 'text/html');
        const page = document.querySelector('[data-catalogue-page]');
        if (!page || !/^\d+$/.test(page.dataset.total || '')) throw new Error('The catalogue response is incomplete.');
        const pageTotal = Number(page.dataset.total);
        if (!Number.isSafeInteger(pageTotal) || (total !== undefined && total !== pageTotal)) {
          throw new Error('The catalogue changed while loading.');
        }
        total = pageTotal;
        page.querySelectorAll('template[data-catalogue-item]').forEach((template) => {
          const card = template.content.querySelector('[data-colour-card]');
          const id = template.dataset.productId;
          if (!id || !card || !card.dataset.colourName || !card.dataset.productLine
            || template.content.children.length !== 1 || template.content.firstElementChild.tagName !== 'LI') {
            throw new Error('The catalogue contains an incomplete product.');
          }
          const existing = records.get(id);
          if (existing && existing.template.innerHTML !== template.innerHTML) throw new Error('The catalogue changed while loading.');
          records.set(id, { template, name: card.dataset.colourName, code: card.dataset.colourCode,
            skus: card.dataset.colourSkus, family: card.dataset.colourFamily,
            finish: card.dataset.colourFinish, line: card.dataset.productLine });
        });
        const next = page.dataset.next;
        url = next ? this.pageURL(next, url, base) : null;
      } while (url);
      if (records.size !== total) throw new Error('The complete catalogue could not be verified.');
      return [...records.values()];
    }

    async search() {
      const generation = ++this.generation;
      const query = normalizeValue(this.input.value);
      if (!query) { this.clear(); return; }
      this.setActive(true);
      this.resetResults();
      this.results.hidden = false;
      this.status.hidden = false;
      this.status.textContent = 'Loading the complete catalogue…';
      this.root.setAttribute('aria-busy', 'true');
      try {
        if (!this.catalogue && !this.loading) {
          this.loading = this.loadCatalogue().then((records) => {
            this.catalogue = records;
            return records;
          }).finally(() => { this.loading = null; });
        }
        const records = this.catalogue || await this.loading;
        if (generation !== this.generation || !this.root.isConnected) return;
        this.matches = records.filter((product) => product.line === this.root.dataset.productLine
          && matchesColour(product, query));
        this.showMore();
        this.root.removeAttribute('aria-busy');
      } catch (error) {
        if (generation !== this.generation || !this.root.isConnected) return;
        this.status.textContent = `${error.message} Select Search to retry.`;
        this.root.removeAttribute('aria-busy');
      }
    }
  }

  const initializedLibraries = new WeakSet();

  const initializeColourLibraries = (root = scope.document) => {
    const selector = '[data-catalogue-search], [data-colour-library]';
    const roots = [...(root?.querySelectorAll?.(selector) || [])];
    if (root?.matches?.(selector)) roots.unshift(root);
    roots.forEach((libraryRoot) => {
      if (initializedLibraries.has(libraryRoot)) return;
      if (libraryRoot.matches('[data-colour-library]') && libraryRoot.querySelector('[data-catalogue-search]')) return;
      initializedLibraries.add(libraryRoot);
      if (libraryRoot.matches('[data-catalogue-search]')) new CatalogueSearch(libraryRoot);
      else new ColourLibrary(libraryRoot);
    });
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { CatalogueSearch, ColourLibrary, classifyColourFamily, matchesColour, normalizeValue, resolveColourFamily, initializeColourLibraries };
  }

  if (scope.document) {
    initializeColourLibraries();
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          initializeColourLibraries(node);
        });
      });
    }).observe(scope.document.documentElement, { childList: true, subtree: true });
  }
})(typeof window !== 'undefined' ? window : globalThis);
